import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import tasksService, { Task } from '../../services/api/tasksService';
import dailyDispatchService, { DailyDispatch, DailyDispatchPacket, DailyDispatchTask } from '../../services/api/dailyDispatchService';
import dfrService, { DailyFieldReport } from '../../services/api/dfrService';
import type { AccountsTransaction } from '../../services/api/roleWorkspaceService';
import type { TaskMaterial } from '../../services/api/taskMaterialsService';
import type { TeamOverviewRow } from '../../services/api/teamService';
import type { InventorySummaryRow } from '../../services/api/resourceService';
import userService, { UserListItem } from '../../services/api/userService';
import { RootState } from '../../store';
import usePmFieldData, {
  compareTasksByExecutionOrder,
  isConstructionExecutionTask,
  isOpenTask,
  taskSequenceValue,
  taskStatusOf,
} from './usePmFieldData';
import { lifecycleLabels } from './workerFieldModeUtils';
import { ACCOUNTS_PERSONNEL_QUERY_KEY, pmFieldPersonnel, pmFieldPersonnelById } from '../../routes/fieldPersonnel';
import PersonnelFolderCard from '../../components/field/PersonnelFolderCard';
import SitePlanMap from '../../components/siteplan/SitePlanMap';
import { workerSpecificationTexts } from '../../utils/taskRoleSpecifications';

type PmFieldView = 'home' | 'lookahead' | 'dispatch' | 'issued' | 'submitted-reports' | 'reviews';

interface PmFieldModePageProps {
  view?: PmFieldView;
}

type LookaheadFilter = 'this-week' | 'next-week' | 'blocked' | 'all';
type TeamDrilldownKey = 'scheduled' | 'inProgress' | 'inspectionPending' | 'completed' | 'openDefects' | 'rework' | 'payment';
type DispatchSelectedWorker = {
  id?: string | null;
  name?: string | null;
  badgeCode?: string | null;
  teamId?: string | null;
  trade?: string | null;
  userEmail?: string | null;
  selectionType?: string | null;
};
type DfrEntryRow = NonNullable<DailyFieldReport['entries']>[number];

const LOOKAHEAD_FILTERS: Array<{ key: LookaheadFilter; label: string }> = [
  { key: 'this-week', label: 'This week' },
  { key: 'next-week', label: 'Next week' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'all', label: 'All open' },
];

const LOOKAHEAD_ROW_CAP = 30;
const GROUP_ROW_CAP = 15;
const PLANNED_WAITING_PAGE_SIZE = 10;
const DRILLDOWN_ROW_CAP = 8;

const formatDate = (value?: string | null) => {
  if (!value) return 'No date';
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'No time';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const humanize = (value?: string | null) => (value ? value.replace(/_/g, ' ') : '');

const personName = (person?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null) => {
  const name = `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim();
  return name || person?.email || 'Unknown';
};

const dfrTaskLabel = (entry: DfrEntryRow) => {
  const code = entry.task?.taskCode;
  const name = entry.task?.name;
  return code && name ? `${code} - ${name}` : name || code || entry.description;
};

const dfrInspectionLabel = (entry: DfrEntryRow) => {
  const number = entry.inspection?.inspectionNumber;
  const title = entry.inspection?.title;
  return number && title ? `${number} - ${title}` : title || number || entry.description;
};

const dayStart = (offsetDays: number) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  return date.getTime();
};

const dueTime = (task: Task) => {
  const raw = task.dueDate ?? task.startDate;
  const time = raw ? new Date(raw).getTime() : Number.NaN;
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

const dueWithin = (task: Task, fromOffset: number, toOffset: number) => {
  const time = dueTime(task);
  return time !== Number.MAX_SAFE_INTEGER && time >= dayStart(fromOffset) && time < dayStart(toOffset);
};

const taskLabel = (task: Task) => (task.taskCode ? `${task.taskCode} - ${task.title}` : task.title);

const teamNameOf = (task: Task) =>
  task.team?.name ?? task.teamAssignments?.find((row) => row.team?.name)?.team?.name ?? 'Unassigned';

const supervisorNameOf = (task: Task) =>
  task.pmIssuedToSupervisor
    ? `${task.pmIssuedToSupervisor.firstName} ${task.pmIssuedToSupervisor.lastName}`.trim()
    : null;

const locationOf = (task: Task) =>
  task.workLocationRef?.name
  ?? task.workLocation
  ?? task.component?.unit?.name
  ?? task.component?.unit?.code
  ?? task.locationId
  ?? 'No location';

const sequenceLabel = (task: Task) =>
  taskSequenceValue(task) === Number.MAX_SAFE_INTEGER ? 'Unsequenced' : `Seq ${taskSequenceValue(task)}`;

const positiveInteger = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

const isLabourResource = (line: TaskMaterial) => {
  const type = `${line.productType ?? ''} ${line.resource?.resourceType ?? ''} ${line.resource?.category ?? ''}`.toLowerCase();
  return type.includes('labour') || type.includes('labor') || type.includes('crew');
};

const plannedCrewOf = (lines: TaskMaterial[]) => {
  const crewNames = lines
    .filter(isLabourResource)
    .map((line) => line.resource?.name)
    .filter((name): name is string => Boolean(name));
  return [...new Set(crewNames)].join(', ') || null;
};

const responsibleFor = (task: Task, lines: TaskMaterial[] = []) => {
  const teamName = teamNameOf(task);
  if (teamName !== 'Unassigned') return teamName;
  const supervisorName = supervisorNameOf(task);
  if (supervisorName) return supervisorName;
  return plannedCrewOf(lines) ?? 'Unassigned';
};

const specSummaryOf = (task: Task) => {
  const specs = workerSpecificationTexts(task);
  return specs.length ? [`Worker specs: ${specs.join('; ')}`] : [];
};

const materialHandlingOf = (task: Task, lines: TaskMaterial[] = []) =>
  task.materialHandling
  ?? lines.find((line) => line.lineSpecification)?.lineSpecification
  ?? 'No handling note';

const statusLabel = (task: Task) => lifecycleLabels[taskStatusOf(task)] ?? humanize(taskStatusOf(task));
const PM_ISSUEABLE_STATUSES = new Set(['SCHEDULED', 'WAITING', 'READY']);
const isConstructionTask = isConstructionExecutionTask;
const isPmHandoffTask = (task: Task) =>
  isConstructionExecutionTask(task) && PM_ISSUEABLE_STATUSES.has(taskStatusOf(task));

interface MaterialStockSummary {
  available: number;
  inUse: number;
  total: number;
  unit: string;
  locations: number;
  lowStock: boolean;
  belowSafetyStock: boolean;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat([], { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 }).format(value);

const formatQuantity = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

const stockUsedPercent = (summary?: MaterialStockSummary | null) =>
  summary && summary.total > 0 ? Math.round((100 * summary.inUse) / summary.total) : null;

const buildMaterialStockSummaries = (rows: InventorySummaryRow[]) => {
  const summaries = new Map<string, MaterialStockSummary>();
  rows.forEach((row) => {
    const current = summaries.get(row.resourceId) ?? {
      available: 0,
      inUse: 0,
      total: 0,
      unit: row.unitOfMeasure,
      locations: 0,
      lowStock: false,
      belowSafetyStock: false,
    };
    current.available += Number(row.quantityAvailable || 0);
    current.inUse += Number(row.quantityInUse || 0);
    current.total += Number(row.totalQuantity || 0);
    current.locations += 1;
    current.lowStock = current.lowStock || row.lowStock;
    current.belowSafetyStock = current.belowSafetyStock || row.belowSafetyStock;
    if (!current.unit && row.unitOfMeasure) current.unit = row.unitOfMeasure;
    summaries.set(row.resourceId, current);
  });
  return summaries;
};

const taskTeamIds = (task: Task) => {
  const ids = new Set<string>();
  if (task.teamId) ids.add(task.teamId);
  task.teamAssignments?.forEach((assignment) => {
    const id = assignment.teamId ?? assignment.team_id ?? assignment.team?.id;
    if (id) ids.add(id);
  });
  return ids;
};

const taskInTeam = (task: Task, teamId: string) => taskTeamIds(task).has(teamId);

const taskDate = (task: Task) =>
  task.completionDate ?? task.actualStartDate ?? task.dueDate ?? task.startDate ?? task.updatedAt ?? task.createdAt;

const taskIssuedDate = (task: Task) => task.pmIssuedAt ?? task.updatedAt ?? task.createdAt;

const dispatchItemMetadata = (item: DailyDispatchTask) =>
  (item.metadata ?? {}) as {
    taskCode?: string;
    taskName?: string;
    workLocation?: string | null;
    selectedWorkers?: DispatchSelectedWorker[];
  };

const transactionTaskId = (transaction: AccountsTransaction) => transaction.taskId ?? transaction.task?.id ?? null;

const sortTasksByDateDesc = (rows: Task[]) =>
  [...rows].sort((a, b) => new Date(taskDate(b) ?? 0).getTime() - new Date(taskDate(a) ?? 0).getTime());

const sortTransactionsByDateDesc = (rows: AccountsTransaction[]) =>
  [...rows].sort((a, b) => new Date(b.transactionDate ?? 0).getTime() - new Date(a.transactionDate ?? 0).getTime());

const dateGroupLabel = (value?: string | null) => {
  if (!value) return 'No date recorded';
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const groupTasksByDate = (rows: Task[]) => {
  const groups = new Map<string, Task[]>();
  sortTasksByDateDesc(rows).forEach((task) => {
    const label = dateGroupLabel(taskDate(task));
    groups.set(label, [...(groups.get(label) ?? []), task]);
  });
  return [...groups.entries()];
};

const groupIssuedTasksByDate = (rows: Task[]) => {
  const groups = new Map<string, Task[]>();
  [...rows]
    .sort((a, b) => new Date(taskIssuedDate(b) ?? 0).getTime() - new Date(taskIssuedDate(a) ?? 0).getTime())
    .forEach((task) => {
      const label = dateGroupLabel(taskIssuedDate(task));
      groups.set(label, [...(groups.get(label) ?? []), task]);
    });
  return [...groups.entries()];
};

const groupTransactionsByDate = (rows: AccountsTransaction[]) => {
  const groups = new Map<string, AccountsTransaction[]>();
  sortTransactionsByDateDesc(rows).forEach((transaction) => {
    const label = dateGroupLabel(transaction.transactionDate);
    groups.set(label, [...(groups.get(label) ?? []), transaction]);
  });
  return [...groups.entries()];
};

const reviewMetric = (
  label: string,
  value: string | number,
  note?: string,
  onClick?: () => void,
  active?: boolean
) => (
  <ButtonBase
    component="div"
    onClick={onClick}
    disabled={!onClick}
    sx={{
      width: '100%',
      border: '1px solid',
      borderColor: active ? 'primary.main' : 'divider',
      borderRadius: 1,
      p: 1,
      minHeight: 76,
      bgcolor: active ? 'action.selected' : 'background.paper',
      display: 'block',
      textAlign: 'left',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : undefined,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography fontWeight={900} sx={{ mt: 0.25, lineHeight: 1.15 }}>
      {value}
    </Typography>
    {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
  </ButtonBase>
);

export default function PmFieldModePage({ view = 'home' }: PmFieldModePageProps) {
  const {
    tasks,
    taskResources,
    teamOverview,
    permits,
    inspections,
    bonusPoints,
    accountsTransactions,
    purchaseOrders,
    inventoryMovements,
    inventorySummary,
    dispatches,
    loading,
    error,
    reload,
  } = usePmFieldData();
  const user = useSelector((state: RootState) => state.auth.user);
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [lookaheadFilter, setLookaheadFilter] = useState<LookaheadFilter>('this-week');
  const [plannedSequenceSearch, setPlannedSequenceSearch] = useState('');
  const [plannedPageSearch, setPlannedPageSearch] = useState('1');
  const [teamDrilldown, setTeamDrilldown] = useState<{ teamId: string; key: TeamDrilldownKey } | null>(null);
  const [supervisors, setSupervisors] = useState<UserListItem[]>([]);
  const [issueTask, setIssueTask] = useState<Task | null>(null);
  const [issueSupervisorId, setIssueSupervisorId] = useState('');
  const [issueNotes, setIssueNotes] = useState('');
  const [issueSaving, setIssueSaving] = useState(false);
  const [recallSavingId, setRecallSavingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submittedReports, setSubmittedReports] = useState<DailyFieldReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<DailyFieldReport | null>(null);
  const [dispatchPacketDetails, setDispatchPacketDetails] = useState<Record<string, DailyDispatchPacket>>({});
  const [loadingDispatchPacketId, setLoadingDispatchPacketId] = useState<string | null>(null);
  const [dispatchPacketError, setDispatchPacketError] = useState<string | null>(null);
  const allowPersonnelSelection = ['admin', 'field_admin'].includes(user?.role ?? '');
  const selectedPersonnel = allowPersonnelSelection
    ? pmFieldPersonnelById(searchParams.get(ACCOUNTS_PERSONNEL_QUERY_KEY))
    : null;

  useEffect(() => {
    let cancelled = false;
    userService.searchUsers({ role: 'supervisor', limit: 50 })
      .then((rows) => {
        if (cancelled) return;
        setSupervisors(rows);
        setIssueSupervisorId((current) => current || rows[0]?.id || '');
      })
      .catch(() => {
        if (!cancelled) setSupervisors([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadSubmittedReports = async () => {
    if (!activeProjectId) return;
    setReportsLoading(true);
    setReportsError(null);
    try {
      const rows = await dfrService.list({ projectId: activeProjectId, status: 'submitted' });
      setSubmittedReports(rows);
    } catch (err: any) {
      setReportsError(err?.response?.data?.message || err?.response?.data?.error || 'Submitted field reports could not be loaded.');
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'submitted-reports') void loadSubmittedReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activeProjectId]);

  const loadDispatchPacketDetails = async (dispatchId: string) => {
    if (dispatchPacketDetails[dispatchId]) return;
    try {
      setLoadingDispatchPacketId(dispatchId);
      setDispatchPacketError(null);
      const packet = await dailyDispatchService.getDispatch(dispatchId);
      setDispatchPacketDetails((current) => ({ ...current, [dispatchId]: packet }));
    } catch (err: any) {
      setDispatchPacketError(err?.response?.data?.message || err?.response?.data?.error || 'Dispatch packet details could not be loaded.');
    } finally {
      setLoadingDispatchPacketId(null);
    }
  };

  const openTasks = useMemo(
    () => tasks
      .filter((task) => isOpenTask(task) && isConstructionExecutionTask(task))
      .sort(compareTasksByExecutionOrder),
    [tasks]
  );
  const issuedSupervisorTasks = useMemo(
    () => tasks
      .filter((task) => isConstructionExecutionTask(task) && Boolean(task.pmIssuedToSupervisorId || task.pmIssuedToSupervisor))
      .sort((a, b) => new Date(taskIssuedDate(b) ?? 0).getTime() - new Date(taskIssuedDate(a) ?? 0).getTime()),
    [tasks]
  );
  const activePermits = useMemo(
    () => permits.filter((permit) => !['closed', 'expired', 'rejected'].includes(permit.status)),
    [permits]
  );
  const activeTeams = useMemo(() => teamOverview.filter((row) => row.isActive), [teamOverview]);
  const workingTeams = useMemo(
    () =>
      activeTeams.filter(
        (row) =>
          row.workload.inProgress + row.workload.scheduled + row.workload.blocked + row.workload.inspectionPending > 0
      ),
    [activeTeams]
  );

  const blockedCount = openTasks.filter((task) => taskStatusOf(task) === 'BLOCKED').length;
  const readinessTasks = openTasks.filter((task) => typeof task.materialsReady === 'boolean');
  const materialsReadyPercent = readinessTasks.length
    ? Math.round((100 * readinessTasks.filter((task) => task.materialsReady).length) / readinessTasks.length)
    : null;
  const stockByResourceId = useMemo(
    () => buildMaterialStockSummaries(inventorySummary),
    [inventorySummary]
  );
  const thisWeekTasks = openTasks.filter((task) => dueWithin(task, 0, 7));
  const executableThisWeek = thisWeekTasks.filter((task) =>
    ['READY', 'IN_PROGRESS', 'PAUSED', 'INSPECTION_PENDING'].includes(taskStatusOf(task))
  );
  const readinessPercent = thisWeekTasks.length
    ? Math.round((100 * executableThisWeek.length) / thisWeekTasks.length)
    : 0;

  const lookaheadTasks = useMemo(() => {
    const filtered =
      lookaheadFilter === 'this-week'
        ? openTasks.filter((task) => dueWithin(task, 0, 7))
        : lookaheadFilter === 'next-week'
          ? openTasks.filter((task) => dueWithin(task, 7, 14))
          : lookaheadFilter === 'blocked'
            ? openTasks.filter((task) => taskStatusOf(task) === 'BLOCKED')
            : openTasks;
    return filtered.slice(0, LOOKAHEAD_ROW_CAP);
  }, [lookaheadFilter, openTasks]);

  const lookaheadResourceRows = useMemo(
    () =>
      lookaheadTasks.flatMap((task) =>
        (taskResources[task.id] ?? []).map((line) => ({
          key: `${task.id}-${line.id}`,
          task: `${sequenceLabel(task)} - ${task.taskCode ?? task.title}`,
          location: locationOf(task),
          resource: line.resource?.name ?? line.resourceId,
          readiness: humanize(line.allocationStatus) || 'unknown',
          handling: line.lineSpecification ?? task.materialHandling ?? 'No handling note',
          type: humanize(line.resource?.resourceType) || '—',
        }))
      ),
    [lookaheadTasks, taskResources]
  );

  const metric = (label: string, value: string, note?: string) => (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, minHeight: 96 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={900} sx={{ mt: 0.5, lineHeight: 1.15 }}>{value}</Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Paper>
  );

  const menuBar = (label: string, detail: string, to: string) => (
    <Button
      component={RouterLink}
      to={to}
      fullWidth
      variant="outlined"
      sx={{ justifyContent: 'space-between', minHeight: 72, px: 2, borderRadius: 1, textAlign: 'left', color: 'text.primary', borderColor: 'divider' }}
    >
      <Box>
        <Typography fontWeight={900}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </Box>
      <Typography fontWeight={900}>Open</Typography>
    </Button>
  );

  const openSelectedPersonnel = (personnelId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'pm');
    nextParams.set(ACCOUNTS_PERSONNEL_QUERY_KEY, personnelId);
    setSearchParams(nextParams);
  };

  const clearSelectedPersonnel = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(ACCOUNTS_PERSONNEL_QUERY_KEY);
    nextParams.set('view', 'pm');
    setSearchParams(nextParams);
  };

  const openIssueDialog = (task: Task) => {
    setIssueTask(task);
    setIssueSupervisorId(task.pmIssuedToSupervisorId || supervisors[0]?.id || '');
    setIssueNotes('');
  };

  const submitIssueToSupervisor = async () => {
    if (!issueTask || !issueSupervisorId) return;
    setIssueSaving(true);
    try {
      const updated = await tasksService.issueToSupervisor(issueTask.id, {
        supervisorUserId: issueSupervisorId,
        notes: issueNotes.trim() || null,
      });
      const supervisorName = updated.pmIssuedToSupervisor
        ? `${updated.pmIssuedToSupervisor.firstName} ${updated.pmIssuedToSupervisor.lastName}`.trim()
        : 'Supervisor';
      setNotice(`${taskLabel(updated)} issued to ${supervisorName}.`);
      setIssueTask(null);
      await reload();
    } catch (err: any) {
      setNotice(err?.response?.data?.message || err?.response?.data?.error || 'Failed to issue task to supervisor.');
    } finally {
      setIssueSaving(false);
    }
  };

  const recallFromSupervisor = async (task: Task) => {
    setRecallSavingId(task.id);
    try {
      await tasksService.recallFromSupervisor(task.id, {
        notes: 'Recalled by Project Manager for edits before reissue.',
      });
      setNotice(`${taskLabel(task)} taken back from supervisor. Open it to edit, then issue it again.`);
      await reload();
    } catch (err: any) {
      setNotice(err?.response?.data?.message || err?.response?.data?.error || 'Failed to take task back from supervisor.');
    } finally {
      setRecallSavingId(null);
    }
  };

  const pmPersonnelIndicatorGrid = (
    <Stack spacing={1.5}>
      <Typography variant="h4" fontWeight={900}>Project Managers</Typography>
      {menuBar('Issued Tasks', 'Supervisor handoffs grouped by issued date with materials', '/field/pm/issued')}
      <Grid container spacing={1.5}>
        {pmFieldPersonnel.map((person) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={person.id}>
            <PersonnelFolderCard
              name={person.name}
              email={person.email}
              credentialHint={person.credentialHint}
              onClick={() => openSelectedPersonnel(person.id)}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  const pmPersonnelContextCard = selectedPersonnel ? (
    <Paper sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">
            Project manager
          </Typography>
          <Typography variant="h5" fontWeight={900}>{selectedPersonnel.name}</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <Chip size="small" label={selectedPersonnel.roleLabel} />
            <Chip size="small" label={selectedPersonnel.team} />
            <Chip size="small" label={`Login: ${selectedPersonnel.email}`} />
            {selectedPersonnel.credentialHint && <Chip size="small" label={selectedPersonnel.credentialHint} />}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${openTasks.length} open tasks`} />
          <Chip size="small" label={`${workingTeams.length} working teams`} />
          <Chip size="small" label={`${activePermits.length} active permits`} />
          <Button size="small" variant="outlined" onClick={clearSelectedPersonnel} sx={{ borderRadius: 1, fontWeight: 900 }}>
            Back to Project Managers
          </Button>
        </Stack>
      </Stack>
    </Paper>
  ) : null;

  const errorAlert = error ? (
    <Alert
      severity="error"
      action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}
    >
      {error}
    </Alert>
  ) : null;

  const noticeAlert = notice ? (
    <Alert severity={notice.startsWith('Failed') ? 'error' : 'success'} onClose={() => setNotice(null)}>
      {notice}
    </Alert>
  ) : null;

  const materialStockMeter = (resourceId: string, unitFallback = '') => {
    const stock = stockByResourceId.get(resourceId);
    if (!stock) return <Chip size="small" variant="outlined" color="warning" label="No stock record" />;
    const percent = stockUsedPercent(stock);
    const unit = stock.unit || unitFallback;
    return (
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" variant="outlined" label={`${formatQuantity(stock.available)} ${unit} available`} />
        <Chip size="small" variant="outlined" label={`${formatQuantity(stock.inUse)} ${unit} in use`} />
        {percent !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 108 }}>
            <Typography variant="caption" fontWeight={900}>{percent}%</Typography>
            <LinearProgress variant="determinate" value={percent} sx={{ width: 72, height: 6, borderRadius: 1 }} />
          </Box>
        )}
        {(stock.lowStock || stock.belowSafetyStock) && (
          <Chip size="small" color="warning" label={stock.belowSafetyStock ? 'Below safety stock' : 'Low stock'} />
        )}
      </Stack>
    );
  };

  const taskCard = (task: Task) => {
    const status = taskStatusOf(task);
    const canIssueToSupervisor = isPmHandoffTask(task);
    const resources = taskResources[task.id] ?? [];
    const details = [
      sequenceLabel(task),
      locationOf(task),
      responsibleFor(task, resources),
      `Due ${formatDate(task.dueDate ?? task.startDate)}`,
    ];
    if (task.pmIssuedToSupervisor) {
      details.push(`Issued to ${task.pmIssuedToSupervisor.firstName} ${task.pmIssuedToSupervisor.lastName}`);
    }
    if (status === 'BLOCKED' && task.blockedReason) details.push(humanize(task.blockedReason));
    const specs = specSummaryOf(task);
    const handling = materialHandlingOf(task, resources);
    return (
      <Paper
        key={task.id}
        variant="outlined"
        sx={{
          p: 1,
          borderRadius: 1,
          color: 'inherit',
          display: 'block',
          textDecoration: 'none',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={900}>{taskLabel(task)}</Typography>
            <Typography variant="caption" color="text.secondary">{details.join(' | ')}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Material: {handling}
            </Typography>
            {resources.filter((line) => !isLabourResource(line)).slice(0, 3).map((line) => (
              <Stack key={line.id} direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ sm: 'center' }} sx={{ mt: 0.5 }}>
                <Typography variant="caption" fontWeight={900}>
                  {line.resource?.name ?? line.resourceId}
                </Typography>
                {materialStockMeter(line.resourceId, line.resource?.unitOfMeasure ?? line.unitOfMeasure ?? '')}
              </Stack>
            ))}
            {specs.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block">
                {specs.join(' | ')}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canIssueToSupervisor && (
              <Button size="small" variant={task.pmIssuedToSupervisor ? 'outlined' : 'contained'} onClick={() => openIssueDialog(task)}>
                {task.pmIssuedToSupervisor ? 'Reissue' : 'Issue to Supervisor'}
              </Button>
            )}
            <Button size="small" component={RouterLink} to={`/field/tasks/${task.id}`} variant="outlined">
              Open
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const dispatchWorkerChips = (workers: DispatchSelectedWorker[], selectionType: 'assigned_crew' | 'alternative') => {
    const filtered = workers.filter((worker) => worker.selectionType === selectionType);
    if (!filtered.length) {
      return <Typography variant="caption" color="text.secondary">None recorded</Typography>;
    }
    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {filtered.map((worker, index) => (
          <Chip
            key={`${selectionType}-${worker.id ?? worker.userEmail ?? worker.name ?? index}`}
            size="small"
            color={selectionType === 'alternative' ? 'warning' : 'default'}
            variant={selectionType === 'alternative' ? 'filled' : 'outlined'}
            label={[
              worker.name || worker.userEmail || 'Worker',
              worker.badgeCode ? `#${worker.badgeCode}` : null,
              selectionType === 'alternative' ? 'Alternative' : null,
            ].filter(Boolean).join(' | ')}
          />
        ))}
      </Stack>
    );
  };

  const renderDispatchPacketDetails = (dispatch: DailyDispatch) => {
    const packet = dispatchPacketDetails[dispatch.id];
    if (loadingDispatchPacketId === dispatch.id) return <LinearProgress sx={{ borderRadius: 1 }} />;
    if (!packet) return null;
    return (
      <Stack spacing={1}>
        {packet.items.map((item) => {
          const metadata = dispatchItemMetadata(item);
          const task = tasks.find((row) => row.id === item.taskId);
          const workers = metadata.selectedWorkers ?? [];
          const crew = item.teamId
            ? teamOverview.find((row) => row.id === item.teamId)?.name
            : null;
          const supervisor = task ? supervisorNameOf(task) : null;
          return (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={900}>
                      {metadata.taskCode || task?.taskCode || `Item ${item.sequenceOrder}`}
                      {' - '}
                      {metadata.taskName || task?.title || 'Task'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Supervisor sent to: {supervisor || 'Not recorded'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Crew received: {crew || (task ? teamNameOf(task) : 'Unassigned')}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`Seq ${item.sequenceOrder}`} />
                    <Chip size="small" label={humanize(item.deliveryStatus)} />
                  </Stack>
                </Stack>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Crew workers</Typography>
                  {dispatchWorkerChips(workers, 'assigned_crew')}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Alternative workers</Typography>
                  {dispatchWorkerChips(workers, 'alternative')}
                </Box>
              </Stack>
            </Paper>
          );
        })}
        {packet.items.length === 0 && <Typography variant="body2" color="text.secondary">No tasks were recorded on this packet.</Typography>}
      </Stack>
    );
  };

  const issuedTaskCard = (task: Task) => {
    const resources = taskResources[task.id] ?? [];
    const supervisorName = task.pmIssuedToSupervisor ? personName(task.pmIssuedToSupervisor) : 'Supervisor not recorded';
    return (
      <Paper key={task.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ md: 'flex-start' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={900}>{taskLabel(task)}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Issued {formatDateTime(taskIssuedDate(task))} | Supervisor: {supervisorName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {locationOf(task)} | {responsibleFor(task, resources)} | {statusLabel(task)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent={{ md: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => recallFromSupervisor(task)}
                disabled={recallSavingId === task.id}
              >
                {recallSavingId === task.id ? 'Taking Back' : 'Take Back'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => openIssueDialog(task)}>
                Reissue
              </Button>
              <Button size="small" component={RouterLink} to={`/field/tasks/${task.id}`} variant="contained">
                Open
              </Button>
            </Stack>
          </Stack>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Table size="small" sx={{ '& td, & th': { py: 0.55, px: 1, fontSize: 12 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Material</TableCell>
                  <TableCell>Specification</TableCell>
                  <TableCell align="right">Required</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resources.length ? resources.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={800}>{line.resource?.resourceCode || line.resourceId}</Typography>
                      <Typography variant="caption" color="text.secondary">{line.resource?.name || 'Material name missing'}</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {materialStockMeter(line.resourceId, line.resource?.unitOfMeasure ?? line.unitOfMeasure ?? '')}
                      </Box>
                    </TableCell>
                    <TableCell>{line.lineSpecification || task.materialHandling || '-'}</TableCell>
                    <TableCell align="right">
                      {Number(line.quantityRequired || 0).toFixed(2)} {line.resource?.unitOfMeasure || line.unitOfMeasure || ''}
                    </TableCell>
                    <TableCell>{humanize(line.allocationStatus) || 'Not checked'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">No materials are attached to this task.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </Paper>
    );
  };

  const teamTasks = (teamId: string) => tasks.filter((task) => taskInTeam(task, teamId));

  const teamTransactions = (teamId: string) => {
    const ids = new Set(teamTasks(teamId).map((task) => task.id));
    return accountsTransactions.filter((transaction) => {
      const linkedTaskId = transactionTaskId(transaction);
      return linkedTaskId ? ids.has(linkedTaskId) : false;
    });
  };

  const materialResourceRows = useMemo(() => {
    const resourceNames = new Map(inventorySummary.map((row) => [row.resourceId, row.resourceName || row.resourceCode]));
    const resourceUnits = new Map(inventorySummary.map((row) => [row.resourceId, row.unitOfMeasure]));
    purchaseOrders.forEach((po) => {
      (po.items ?? []).forEach((item) => {
        if (item.resourceId && item.resource?.name) resourceNames.set(item.resourceId, item.resource.name);
        if (item.resourceId && item.resource?.unitOfMeasure) resourceUnits.set(item.resourceId, item.resource.unitOfMeasure);
      });
    });
    Object.values(taskResources).flat().forEach((line) => {
      if (line.resourceId && line.resource?.name) resourceNames.set(line.resourceId, line.resource.name);
      if (line.resourceId && line.resource?.unitOfMeasure) resourceUnits.set(line.resourceId, line.resource.unitOfMeasure);
    });
    return inventoryMovements
      .filter((movement) => ['issue', 'consumption'].includes(String(movement.movementType).toLowerCase()))
      .map((movement) => {
        const task = tasks.find((row) => row.id === movement.taskId);
        const metadata = movement.metadata ?? {};
        const metadataCrew = typeof metadata.crewName === 'string' ? metadata.crewName : '';
        const crew = metadataCrew || (task ? teamNameOf(task) : 'Unassigned');
        const metadataResourceName = typeof metadata.resourceName === 'string' ? metadata.resourceName : '';
        const readableMaterial = metadataResourceName || resourceNames.get(movement.resourceId) || 'Material name missing';
        return {
          id: movement.id,
          crew,
          material: readableMaterial,
          quantity: Math.abs(Number(movement.quantity || 0)),
          unit: resourceUnits.get(movement.resourceId) ?? '',
          taskLabel: task ? taskLabel(task) : '',
          date: movement.createdAt,
        };
      })
      .filter((row) => row.quantity > 0)
      .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
        || a.crew.localeCompare(b.crew)
        || a.material.localeCompare(b.material));
  }, [inventoryMovements, inventorySummary, purchaseOrders, taskResources, tasks]);

  const orderedMaterialRows = useMemo(() => purchaseOrders
    .filter((po) => !['cancelled', 'closed', 'received'].includes(po.status))
    .flatMap((po) => (po.items ?? []).map((item) => {
      const ordered = Number(item.quantityOrdered || 0);
      const received = Number(item.quantityReceived || 0);
      return {
        id: item.id,
        poNumber: po.poNumber,
        supplier: po.supplier?.name ?? po.supplierId,
        status: po.status,
        material: item.resource?.name || item.description || 'Material name missing',
        quantityOrdered: ordered,
        quantityReceived: received,
        quantityPending: Math.max(0, ordered - received),
        unit: item.resource?.unitOfMeasure ?? '',
        expectedDate: item.expectedDeliveryDate ?? po.expectedDeliveryDate ?? po.createdAt,
        totalValue: ordered * Number(item.unitCost || 0),
      };
    }))
    .filter((row) => row.quantityPending > 0)
    .sort((a, b) => new Date(a.expectedDate || 0).getTime() - new Date(b.expectedDate || 0).getTime()
      || a.material.localeCompare(b.material)), [purchaseOrders]);

  const orderedMaterialsByDate = useMemo(() => {
    const groups = new Map<string, typeof orderedMaterialRows>();
    orderedMaterialRows.forEach((row) => {
      const label = dateGroupLabel(row.expectedDate);
      groups.set(label, [...(groups.get(label) ?? []), row]);
    });
    return [...groups.entries()].sort(([, aRows], [, bRows]) =>
      new Date(aRows[0]?.expectedDate || 0).getTime() - new Date(bRows[0]?.expectedDate || 0).getTime()
    );
  }, [orderedMaterialRows]);

  const materialResourcesByDate = useMemo(() => {
    const groups = new Map<string, typeof materialResourceRows>();
    materialResourceRows.forEach((row) => {
      const label = dateGroupLabel(row.date);
      groups.set(label, [...(groups.get(label) ?? []), row]);
    });
    return [...groups.entries()].sort(([, aRows], [, bRows]) =>
      new Date(bRows[0]?.date || 0).getTime() - new Date(aRows[0]?.date || 0).getTime()
    );
  }, [materialResourceRows]);

  const groupMaterialRowsByCrew = (rows: typeof materialResourceRows) => {
    const groups = new Map<string, typeof materialResourceRows>();
    rows.forEach((row) => groups.set(row.crew, [...(groups.get(row.crew) ?? []), row]));
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  };

  const teamPaymentTotals = (row: TeamOverviewRow) => {
    const transactions = teamTransactions(row.id);
    const paidTransactions = transactions.filter((transaction) => ['recorded', 'reconciled'].includes(transaction.status));
    const pendingTransactions = transactions.filter((transaction) => transaction.status === 'draft');
    const rejectedTransactions = transactions.filter((transaction) => transaction.status === 'rejected');
    const recordedTotal = paidTransactions.reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0);
    const pendingTotal = pendingTransactions.reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0);
    return {
      transactions,
      paidTransactions,
      pendingTransactions,
      rejectedTransactions,
      totalPaid: recordedTotal || row.payment.totalPaid,
      totalApproved: recordedTotal + pendingTotal || row.payment.totalApproved,
    };
  };

  const drilldownRows = (teamId: string, key: TeamDrilldownKey) => {
    const rows = teamTasks(teamId);
    if (key === 'scheduled') return sortTasksByDateDesc(rows.filter((task) => taskStatusOf(task) === 'SCHEDULED'));
    if (key === 'inProgress') return sortTasksByDateDesc(rows.filter((task) => taskStatusOf(task) === 'IN_PROGRESS'));
    if (key === 'inspectionPending') return sortTasksByDateDesc(rows.filter((task) => taskStatusOf(task) === 'INSPECTION_PENDING'));
    if (key === 'completed') return sortTasksByDateDesc(rows.filter((task) => taskStatusOf(task) === 'INSPECTION_PASSED'));
    if (key === 'openDefects') {
      return sortTasksByDateDesc(
        rows.filter((task) =>
          task.defects?.some((defect) => ['open', 'assigned', 'fixing', 'recheck_pending'].includes(String(defect.status ?? '').toLowerCase()))
        )
      );
    }
    if (key === 'rework') {
      return sortTasksByDateDesc(
        rows.filter((task) =>
          taskStatusOf(task) === 'INSPECTION_FAILED' ||
          Number((task as Task & { reworkCount?: number }).reworkCount ?? 0) > 0 ||
          task.defects?.some((defect) => defect.remedialTaskId)
        )
      );
    }
    return sortTransactionsByDateDesc(teamTransactions(teamId));
  };

  const drilldownTitle = (key: TeamDrilldownKey) => ({
    scheduled: 'Scheduled',
    inProgress: 'In Progress',
    inspectionPending: 'Inspection Pending',
    completed: 'Completed',
    openDefects: 'Open Defects',
    rework: 'Rework',
    payment: 'Payment',
  }[key]);

  const renderTaskDrilldown = (rows: Task[]) => {
    const limitedRows = rows.slice(0, DRILLDOWN_ROW_CAP * 3);
    return (
      <Stack spacing={1}>
        {groupTasksByDate(limitedRows).map(([dateLabel, dayRows], index) => (
          <Accordion key={dateLabel} defaultExpanded={index === 0} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                <Typography fontWeight={900}>{dateLabel}</Typography>
                <Chip size="small" label={`${dayRows.length} item${dayRows.length === 1 ? '' : 's'}`} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 720, '& td, & th': { py: 0.55, px: 0.75, fontSize: 12 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Payment</TableCell>
                      <TableCell>Defect / Note</TableCell>
                      <TableCell align="right">Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dayRows.map((task) => (
                      <TableRow key={task.id} hover>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" fontWeight={900} noWrap>{taskLabel(task)}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{teamNameOf(task)}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={statusLabel(task)} /></TableCell>
                        <TableCell>{humanize(task.paymentStatus) || 'No payment status'}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {task.defects?.[0]
                              ? `${task.defects[0].title}${task.defects[0].description ? ` - ${task.defects[0].description}` : ''}`
                              : task.notes || 'No note'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button component={RouterLink} to={`/field/tasks/${task.id}`} size="small" variant="outlined" sx={{ borderRadius: 1, fontWeight: 900 }}>
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
        {rows.length > limitedRows.length && (
          <Typography variant="caption" color="text.secondary">Showing {limitedRows.length} of {rows.length} matching items, newest first.</Typography>
        )}
      </Stack>
    );
  };

  const renderPaymentDrilldown = (rows: AccountsTransaction[]) => {
    const limitedRows = rows.slice(0, DRILLDOWN_ROW_CAP * 3);
    return (
      <Stack spacing={1}>
        {groupTransactionsByDate(limitedRows).map(([dateLabel, dayRows], index) => (
          <Accordion key={dateLabel} defaultExpanded={index === 0} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                <Typography fontWeight={900}>{dateLabel}</Typography>
                <Chip size="small" label={formatCurrency(dayRows.reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0))} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 720, '& td, & th': { py: 0.55, px: 0.75, fontSize: 12 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dayRows.map((transaction) => (
                      <TableRow key={transaction.id} hover>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" fontWeight={900} noWrap>
                            {transaction.task?.taskCode ? `${transaction.task.taskCode} - ${transaction.task.name}` : 'Project transaction'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{humanize(transaction.type)}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap>{transaction.description}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={humanize(transaction.status)} /></TableCell>
                        <TableCell align="right"><Typography fontWeight={900}>{formatCurrency(Number(transaction.amount || 0))}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
        {rows.length > limitedRows.length && (
          <Typography variant="caption" color="text.secondary">Showing {limitedRows.length} of {rows.length} payment records, newest first.</Typography>
        )}
      </Stack>
    );
  };

  const renderMaterialResourcesReview = () => (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
          <Typography fontWeight={900}>Material Resources</Typography>
          <Chip size="small" label={`${materialResourceRows.length} issued / ${orderedMaterialRows.length} ordered`} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <Accordion variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                <Typography fontWeight={900}>Ordered / Recoup Pending</Typography>
                <Chip size="small" label={`${orderedMaterialRows.length} material${orderedMaterialRows.length === 1 ? '' : 's'}`} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {orderedMaterialsByDate.length ? orderedMaterialsByDate.map(([date, rows]) => (
                  <Accordion key={`ordered-${date}`} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                        <Typography fontWeight={900}>Expected {date}</Typography>
                        <Chip size="small" label={`${rows.length} line${rows.length === 1 ? '' : 's'}`} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small" sx={{ '& td, & th': { py: 0.55, px: 0.75, fontSize: 12 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Material Purchased</TableCell>
                            <TableCell>Supplier</TableCell>
                            <TableCell>PO</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Pending</TableCell>
                            <TableCell align="right">Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>{row.material}</TableCell>
                              <TableCell>{row.supplier}</TableCell>
                              <TableCell>{row.poNumber}</TableCell>
                              <TableCell><Chip size="small" label={humanize(row.status)} /></TableCell>
                              <TableCell align="right">{row.quantityPending} {row.unit}</TableCell>
                              <TableCell align="right">{formatCurrency(row.totalValue)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )) : (
                  <Typography variant="body2" color="text.secondary">
                    No ordered or recoup materials are currently pending delivery.
                  </Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                <Typography fontWeight={900}>Issued to Crews</Typography>
                <Chip size="small" label={`${materialResourceRows.length} issue${materialResourceRows.length === 1 ? '' : 's'}`} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
          {materialResourcesByDate.length ? materialResourcesByDate.map(([date, rows]) => (
            <Accordion key={date} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                  <Typography fontWeight={900}>{date}</Typography>
                  <Chip size="small" label={`${rows.length} issue${rows.length === 1 ? '' : 's'}`} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {groupMaterialRowsByCrew(rows).map(([crew, crewRows]) => {
                    const materialCount = new Set(crewRows.map((row) => row.material)).size;
                    const issuedSummary = [...crewRows.reduce((totals, row) => {
                      const key = `${row.material}|${row.unit}`;
                      totals.set(key, (totals.get(key) ?? 0) + Number(row.quantity || 0));
                      return totals;
                    }, new Map<string, number>()).entries()]
                      .map(([key, quantity]) => {
                        const [material, unit] = key.split('|');
                        return `${quantity} ${unit} ${material}`.trim();
                      })
                      .join(', ');
                    return (
                      <Accordion key={`${date}-${crew}`} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                            <Typography fontWeight={900}>{crew}</Typography>
                            <Chip size="small" label={`${materialCount} material${materialCount === 1 ? '' : 's'} issued`} />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Table size="small" sx={{ '& td, & th': { py: 0.55, px: 0.75, fontSize: 12 } }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Material</TableCell>
                                <TableCell>Task</TableCell>
                                <TableCell align="right">Issued</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {crewRows.map((row) => (
                                <TableRow key={row.id}>
                                  <TableCell>{row.material}</TableCell>
                                  <TableCell>{row.taskLabel || '-'}</TableCell>
                                  <TableCell align="right">{row.quantity} {row.unit}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          <Typography variant="caption" color="text.secondary">
                            Total issued by this crew on {date}: {issuedSummary || '-'}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )) : (
            <Typography variant="body2" color="text.secondary">
              No material issues have been recorded against crews yet.
            </Typography>
          )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const renderTeamDrilldown = (row: TeamOverviewRow) => {
    if (!teamDrilldown || teamDrilldown.teamId !== row.id) return null;
    const rows = drilldownRows(row.id, teamDrilldown.key);
    const isPayment = teamDrilldown.key === 'payment';
    return (
      <Paper variant="outlined" sx={{ mt: 1, p: 1.25, borderRadius: 1, bgcolor: 'background.default' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography fontWeight={900}>{drilldownTitle(teamDrilldown.key)} Details</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.name} | {Array.isArray(rows) ? rows.length : 0} matching records
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={() => setTeamDrilldown(null)} sx={{ borderRadius: 1, fontWeight: 900 }}>
            Close
          </Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {rows.length ? (
          isPayment
            ? renderPaymentDrilldown(rows as AccountsTransaction[])
            : renderTaskDrilldown(rows as Task[])
        ) : (
          <Typography variant="body2" color="text.secondary">No matching records for this crew yet.</Typography>
        )}
      </Paper>
    );
  };

  if (loading && !tasks.length) {
    return (
      <Stack spacing={1.5}>
        <Typography variant="h4" fontWeight={900}>Project Management</Typography>
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      </Stack>
    );
  }

  if (view === 'home' && allowPersonnelSelection && !selectedPersonnel) {
    return pmPersonnelIndicatorGrid;
  }

  if (view === 'lookahead') {
    return (
      <Stack spacing={1.5}>
        {pmPersonnelContextCard}
        <Typography variant="h4" fontWeight={900}>Project Management</Typography>
        <Typography variant="h6" fontWeight={900}>Resource Readiness</Typography>
        {errorAlert}
        {noticeAlert}
        <Paper sx={{ p: 1, borderRadius: 1 }}>
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
            {LOOKAHEAD_FILTERS.map((filter) => (
              <Chip
                key={filter.key}
                label={filter.label}
                variant={filter.key === lookaheadFilter ? 'filled' : 'outlined'}
                onClick={() => setLookaheadFilter(filter.key)}
              />
            ))}
          </Stack>
        </Paper>
        <Paper sx={{ p: 1.5, borderRadius: 1 }}>
          <Typography fontWeight={900} sx={{ mb: 1 }}>Tasks</Typography>
          {lookaheadTasks.length ? (
            <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Responsible</TableCell>
                  <TableCell>Material / Review</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lookaheadTasks.map((task) => {
                  const resources = taskResources[task.id] ?? [];
                  const specs = specSummaryOf(task);
                  return (
                    <TableRow key={task.id} hover>
                      <TableCell>{sequenceLabel(task)}</TableCell>
                      <TableCell>{taskLabel(task)}</TableCell>
                      <TableCell>{formatDate(task.dueDate ?? task.startDate)}</TableCell>
                      <TableCell>{locationOf(task)}</TableCell>
                      <TableCell>{responsibleFor(task, resources)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">{materialHandlingOf(task, resources)}</Typography>
                        {specs.length > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {specs.join(' | ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell><Chip size="small" label={statusLabel(task)} /></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                          {isPmHandoffTask(task) && (
                            <Button
                              size="small"
                              variant={task.pmIssuedToSupervisor ? 'outlined' : 'contained'}
                              onClick={() => openIssueDialog(task)}
                              sx={{ borderRadius: 1, fontWeight: 900, whiteSpace: 'nowrap' }}
                            >
                              {task.pmIssuedToSupervisor ? 'Reissue' : 'Send to Supervisor'}
                            </Button>
                          )}
                          <Button
                            component={RouterLink}
                            to={`/field/tasks/${task.id}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1, fontWeight: 900 }}
                          >
                            Open
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">No open tasks in this window.</Typography>
          )}
        </Paper>
        <Paper sx={{ p: 1.5, borderRadius: 1 }}>
          <Typography fontWeight={900} sx={{ mb: 1 }}>Resources Linked To Task</Typography>
          {lookaheadResourceRows.length ? (
            <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Readiness</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Handling / Spec</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lookaheadResourceRows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.task}</TableCell>
                    <TableCell>{row.location}</TableCell>
                    <TableCell>{row.resource}</TableCell>
                    <TableCell>{row.readiness}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.handling}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No resource allocations recorded against the nearest upcoming tasks.
            </Typography>
          )}
        </Paper>
      </Stack>
    );
  }

  if (view === 'issued') {
    const issuedGroups = groupIssuedTasksByDate(issuedSupervisorTasks);

    return (
      <Stack spacing={1.5}>
        {pmPersonnelContextCard}
        <Typography variant="h4" fontWeight={900}>Project Management</Typography>
        <Typography variant="h6" fontWeight={900}>Issued Tasks</Typography>
        {errorAlert}
        {noticeAlert}
        <Accordion disableGutters defaultExpanded variant="outlined" sx={{ borderRadius: 1, bgcolor: '#fff', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
              <Box>
                <Typography fontWeight={900}>Tasks Issued to Supervisors</Typography>
                <Typography variant="caption" color="text.secondary">
                  {issuedSupervisorTasks.length} active supervisor handoff{issuedSupervisorTasks.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Chip size="small" label={issuedSupervisorTasks.length} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.25}>
              {issuedGroups.length ? issuedGroups.map(([label, rows]) => (
                <Accordion key={`issued-${label}`} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                      <Typography fontWeight={900}>Issued - {label}</Typography>
                      <Chip size="small" label={`${rows.length} task${rows.length === 1 ? '' : 's'}`} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {rows.map(issuedTaskCard)}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )) : (
                <Alert severity="info">No tasks have been issued to supervisors yet.</Alert>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Dialog open={Boolean(issueTask)} onClose={() => setIssueTask(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Issue Task to Supervisor</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography fontWeight={900}>{issueTask ? taskLabel(issueTask) : ''}</Typography>
              {issueTask && <SitePlanMap task={issueTask} compact />}
              <TextField
                select
                fullWidth
                label="Supervisor"
                value={issueSupervisorId}
                onChange={(event) => setIssueSupervisorId(event.target.value)}
              >
                {supervisors.map((supervisor) => (
                  <MenuItem key={supervisor.id} value={supervisor.id}>
                    {supervisor.firstName} {supervisor.lastName} - {supervisor.email}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="PM instructions"
                value={issueNotes}
                onChange={(event) => setIssueNotes(event.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIssueTask(null)}>Cancel</Button>
            <Button variant="contained" onClick={submitIssueToSupervisor} disabled={issueSaving || !issueSupervisorId}>
              Issue
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  if (view === 'dispatch') {
    const dispatchGroups: Array<[string, Task[]]> = [
      ['Planned / Waiting Tasks', openTasks.filter((task) => isConstructionTask(task) && ['SCHEDULED', 'WAITING'].includes(taskStatusOf(task)))],
      ['Ready Tasks', openTasks.filter((task) => taskStatusOf(task) === 'READY')],
      ['Blocked Tasks', openTasks.filter((task) => taskStatusOf(task) === 'BLOCKED')],
      ['In Progress Tasks', openTasks.filter((task) => ['IN_PROGRESS', 'PAUSED'].includes(taskStatusOf(task)))],
      ['Tasks To Be Inspected', openTasks.filter((task) => ['INSPECTION_PENDING', 'INSPECTION_FAILED'].includes(taskStatusOf(task)))],
    ];
    const recentDispatches = [...dispatches]
      .sort((a, b) => (b.dispatchDate ?? '').localeCompare(a.dispatchDate ?? ''))
      .slice(0, 3);

    return (
      <Stack spacing={1.5}>
        {pmPersonnelContextCard}
        <Typography variant="h4" fontWeight={900}>Project Management</Typography>
        <Typography variant="h6" fontWeight={900}>Task Dispatch Board</Typography>
        {errorAlert}
        {noticeAlert}
        <Accordion disableGutters variant="outlined" sx={{ borderRadius: 1, bgcolor: '#fff', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
              <Typography fontWeight={900}>Recent Dispatch Packets</Typography>
              <Chip size="small" label={recentDispatches.length} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {recentDispatches.length ? (
              <Stack spacing={1}>
                {recentDispatches.map((dispatch) => (
                  <Accordion
                    key={dispatch.id}
                    disableGutters
                    variant="outlined"
                    onChange={(_, expanded) => {
                      if (expanded) void loadDispatchPacketDetails(dispatch.id);
                    }}
                    sx={{ borderRadius: 1, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ width: '100%', pr: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={900}>{dispatch.title || dispatch.dispatchDate}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(dispatch.dispatchDate)}
                            {dispatch.sentAt ? ` | Sent ${formatDateTime(dispatch.sentAt)}` : ''}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={humanize(dispatch.status)} />
                          <Chip size="small" variant="outlined" label={`${dispatch.deliverySummary?.itemCount ?? 0} task${(dispatch.deliverySummary?.itemCount ?? 0) === 1 ? '' : 's'}`} />
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {dispatchPacketError && <Alert severity="warning" sx={{ mb: 1 }}>{dispatchPacketError}</Alert>}
                      {renderDispatchPacketDetails(dispatch)}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No dispatch packets generated yet.</Typography>
            )}
          </AccordionDetails>
        </Accordion>
        {dispatchGroups.map(([label, rows]) => (
          <Accordion key={label} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                <Typography fontWeight={900}>{label}</Typography>
                <Chip size="small" label={rows.length} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {label === 'Planned / Waiting Tasks' ? (() => {
                const pageCount = Math.max(1, Math.ceil(rows.length / PLANNED_WAITING_PAGE_SIZE));
                const requestedSequence = positiveInteger(plannedSequenceSearch);
                const requestedPage = positiveInteger(plannedPageSearch) ?? 1;
                const sequenceIndex = requestedSequence
                  ? rows.findIndex((task) => {
                      const sequence = taskSequenceValue(task);
                      return sequence !== Number.MAX_SAFE_INTEGER && sequence >= requestedSequence;
                    })
                  : -1;
                const activePage = requestedSequence
                  ? (sequenceIndex === -1 ? pageCount : Math.min(pageCount, Math.max(1, Math.floor(sequenceIndex / PLANNED_WAITING_PAGE_SIZE) + 1)))
                  : Math.min(pageCount, Math.max(1, requestedPage));
                const start = (activePage - 1) * PLANNED_WAITING_PAGE_SIZE;
                const visibleRows = rows.slice(start, start + PLANNED_WAITING_PAGE_SIZE);
                return (
                  <Stack spacing={1}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <TextField
                        size="small"
                        type="number"
                        label="Sequence number"
                        value={plannedSequenceSearch}
                        onChange={(event) => {
                          setPlannedSequenceSearch(event.target.value);
                          setPlannedPageSearch('');
                        }}
                        inputProps={{ min: 1 }}
                        sx={{ maxWidth: 180 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={`Page number (1-${pageCount})`}
                        value={plannedPageSearch}
                        onChange={(event) => {
                          setPlannedPageSearch(event.target.value);
                          setPlannedSequenceSearch('');
                        }}
                        inputProps={{ min: 1, max: pageCount }}
                        sx={{ maxWidth: 180 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={activePage <= 1}
                        onClick={() => {
                          setPlannedSequenceSearch('');
                          setPlannedPageSearch(String(activePage - 1));
                        }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={activePage >= pageCount}
                        onClick={() => {
                          setPlannedSequenceSearch('');
                          setPlannedPageSearch(String(activePage + 1));
                        }}
                      >
                        Next
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Page {activePage} of {pageCount} | Showing {visibleRows.length ? start + 1 : 0}-{Math.min(start + visibleRows.length, rows.length)} of {rows.length}
                      </Typography>
                    </Stack>
                    {requestedSequence && sequenceIndex === -1 && (
                      <Alert severity="info">No exact or later sequence was found. Showing the last page.</Alert>
                    )}
                    {visibleRows.length ? visibleRows.map(taskCard) : (
                      <Typography variant="body2" color="text.secondary">No tasks in this queue.</Typography>
                    )}
                  </Stack>
                );
              })() : (
                <Stack spacing={1}>
                  {rows.length ? rows.slice(0, GROUP_ROW_CAP).map(taskCard) : (
                    <Typography variant="body2" color="text.secondary">No tasks in this queue.</Typography>
                  )}
                  {rows.length > GROUP_ROW_CAP && (
                    <Typography variant="caption" color="text.secondary">
                      Showing {GROUP_ROW_CAP} of {rows.length} tasks.
                    </Typography>
                  )}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
        <Dialog open={Boolean(issueTask)} onClose={() => setIssueTask(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Issue Task to Supervisor</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography fontWeight={900}>{issueTask ? taskLabel(issueTask) : ''}</Typography>
              {issueTask && <SitePlanMap task={issueTask} compact />}
              <TextField
                select
                fullWidth
                label="Supervisor"
                value={issueSupervisorId}
                onChange={(event) => setIssueSupervisorId(event.target.value)}
              >
                {supervisors.map((supervisor) => (
                  <MenuItem key={supervisor.id} value={supervisor.id}>
                    {supervisor.firstName} {supervisor.lastName} - {supervisor.email}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="PM instructions"
                value={issueNotes}
                onChange={(event) => setIssueNotes(event.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIssueTask(null)}>Cancel</Button>
            <Button variant="contained" onClick={submitIssueToSupervisor} disabled={issueSaving || !issueSupervisorId}>
              Issue
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  if (view === 'submitted-reports') {
    const selectedEntries = selectedReport?.entries ?? [];
    const completedTaskEntries = selectedEntries.filter((entry) => entry.entryType === 'progress' && entry.taskId);
    const siteInspectionEntries = selectedEntries.filter((entry) => entry.entryType === 'inspection');
    const otherReportEntries = selectedEntries.filter((entry) =>
      !(entry.entryType === 'progress' && entry.taskId) && entry.entryType !== 'inspection'
    );

    return (
      <Stack spacing={1.5}>
        {pmPersonnelContextCard}
        <Typography variant="h4" fontWeight={900}>Project Management</Typography>
        <Typography variant="h6" fontWeight={900}>Submitted Field Reports</Typography>
        {noticeAlert}
        {reportsError && <Alert severity="error" onClose={() => setReportsError(null)}>{reportsError}</Alert>}
        {reportsLoading && <LinearProgress />}
        <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
          <Table size="small" sx={{ '& td, & th': { py: 0.7, px: 1, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Report date</TableCell>
                <TableCell>Submitted by</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Weather</TableCell>
                <TableCell>Manpower</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Inspections</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell align="right">Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submittedReports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{formatDate(report.reportDate)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={800}>{personName(report.author)}</Typography>
                    <Typography variant="caption" color="text.secondary">{report.author?.role ? humanize(report.author.role) : report.author?.email ?? '-'}</Typography>
                  </TableCell>
                  <TableCell>{report.submittedAt ? formatDateTime(report.submittedAt) : '-'}</TableCell>
                  <TableCell>{report.weather || '-'}</TableCell>
                  <TableCell>{report.manpowerCount}</TableCell>
                  <TableCell>{(report.entries ?? []).filter((entry) => entry.entryType === 'progress' && entry.taskId).length}</TableCell>
                  <TableCell>{(report.entries ?? []).filter((entry) => entry.entryType === 'inspection').length}</TableCell>
                  <TableCell>{report.workSummary || '-'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => setSelectedReport(report)}>Open</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!submittedReports.length && !reportsLoading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary">No submitted field reports are waiting for review.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Dialog open={Boolean(selectedReport)} onClose={() => setSelectedReport(null)} maxWidth="md" fullWidth>
          <DialogTitle>Submitted Field Report</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip label={`Date ${selectedReport ? formatDate(selectedReport.reportDate) : '-'}`} />
                <Chip label={`Submitted by ${personName(selectedReport?.author)}`} />
                {selectedReport?.submittedAt && <Chip label={`Submitted ${formatDateTime(selectedReport.submittedAt)}`} />}
                <Chip label={`Manpower ${selectedReport?.manpowerCount ?? 0}`} />
                <Chip label={`Observed weather ${selectedReport?.weather || 'Not recorded'}`} />
              </Stack>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Observed weather</Typography>
                    <Typography sx={{ mt: 0.5, fontWeight: 800 }}>{selectedReport?.weather || 'Not recorded'}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Manpower</Typography>
                    <Typography sx={{ mt: 0.5, fontWeight: 800 }}>{selectedReport?.manpowerCount ?? 0}</Typography>
                  </Paper>
                </Grid>
              </Grid>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Work summary</Typography>
                <Typography sx={{ mt: 0.5 }}>{selectedReport?.workSummary || 'No work summary entered.'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Weather impact, delays, and controls</Typography>
                <Typography sx={{ mt: 0.5 }}>{selectedReport?.delaysConstraints || 'No weather impact, delay, or control notes entered.'}</Typography>
              </Paper>

              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontWeight: 900 }}>Completed tasks</Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Report note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completedTaskEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{dfrTaskLabel(entry)}</TableCell>
                        <TableCell>{humanize(entry.task?.lifecycleStatus ?? '') || '-'}</TableCell>
                        <TableCell>{entry.description}</TableCell>
                      </TableRow>
                    ))}
                    {!completedTaskEntries.length && (
                      <TableRow><TableCell colSpan={3}>No completed tasks were attached to this report.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>

              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontWeight: 900 }}>Site inspections</Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Inspection</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {siteInspectionEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{dfrInspectionLabel(entry)}</TableCell>
                        <TableCell>{humanize(entry.inspection?.status ?? '') || '-'}</TableCell>
                        <TableCell>{entry.inspection?.completedAt ? formatDateTime(entry.inspection.completedAt) : '-'}</TableCell>
                        <TableCell>{entry.inspection?.resultSummary || entry.description}</TableCell>
                      </TableRow>
                    ))}
                    {!siteInspectionEntries.length && (
                      <TableRow><TableCell colSpan={4}>No site inspections were attached to this report.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>

              {otherReportEntries.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Evidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {otherReportEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{humanize(entry.entryType)}</TableCell>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell>{entry.fieldEvidenceId || entry.documentId || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedReport(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  if (view === 'reviews') {
    const teamPerformanceRows = [...activeTeams].sort((a, b) => a.name.localeCompare(b.name));
    const bonusRows = [...(bonusPoints?.rows ?? [])].sort((a, b) =>
      (a.teamName ?? '').localeCompare(b.teamName ?? '') || (b.periodEnd ?? '').localeCompare(a.periodEnd ?? '')
    );
    const inspectionRows = inspections
      .filter((row) => ['planned', 'scheduled', 'in_progress', 'failed'].includes(row.status))
      .sort((a, b) =>
        (a.scheduledAt ?? a.createdAt ?? '').localeCompare(b.scheduledAt ?? b.createdAt ?? '') ||
        a.title.localeCompare(b.title)
      );
    const permitRows = activePermits
      .sort((a, b) => (a.expiryDate ?? '').localeCompare(b.expiryDate ?? '') || a.title.localeCompare(b.title));

    return (
      <Stack spacing={1.5}>
        {pmPersonnelContextCard}
        <Typography variant="h4" fontWeight={900}>Project Management</Typography>
        <Typography variant="h6" fontWeight={900}>Reviews</Typography>
        {errorAlert}

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
              <Typography fontWeight={900}>Team Performance Reviews</Typography>
              <Chip size="small" label={teamPerformanceRows.length} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {teamPerformanceRows.length ? teamPerformanceRows.map((row) => {
                const firstPassYield = row.performance.firstPassYieldPercent === null
                  ? 'Not measured'
                  : `${row.performance.firstPassYieldPercent}%`;
                const paymentTotals = teamPaymentTotals(row);
                const activeMetric = (key: TeamDrilldownKey) =>
                  teamDrilldown?.teamId === row.id && teamDrilldown.key === key;
                const toggleMetric = (key: TeamDrilldownKey) => {
                  setTeamDrilldown((current) =>
                    current?.teamId === row.id && current.key === key ? null : { teamId: row.id, key }
                  );
                };
                return (
                  <Accordion key={row.id} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        sx={{ width: '100%', pr: 1 }}
                      >
                        <Box>
                          <Typography fontWeight={900}>{row.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {humanize(row.teamType)} | Lead: {row.teamLeadName ?? 'Not assigned'}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={`${row.memberCount} members`} />
                          <Chip size="small" label={`${row.workload.inProgress} in progress`} />
                          <Chip size="small" label={`${row.performance.openDefects} open defects`} />
                          <Chip size="small" label={`Yield ${firstPassYield}`} />
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1}>
                        <Grid item xs={6} md={3}>{reviewMetric('Scheduled', row.workload.scheduled, undefined, () => toggleMetric('scheduled'), activeMetric('scheduled'))}</Grid>
                        <Grid item xs={6} md={3}>{reviewMetric('In Progress', row.workload.inProgress, undefined, () => toggleMetric('inProgress'), activeMetric('inProgress'))}</Grid>
                        <Grid item xs={6} md={3}>{reviewMetric('Inspection Pending', row.workload.inspectionPending, undefined, () => toggleMetric('inspectionPending'), activeMetric('inspectionPending'))}</Grid>
                        <Grid item xs={6} md={3}>{reviewMetric('Completed', row.workload.completed, undefined, () => toggleMetric('completed'), activeMetric('completed'))}</Grid>
                        <Grid item xs={6} md={3}>{reviewMetric('First Pass Yield', firstPassYield, `${row.performance.tasksTouched} tasks touched`)}</Grid>
                        <Grid item xs={6} md={3}>{reviewMetric('Open Defects', row.performance.openDefects, `${row.performance.closedDefects} closed`, () => toggleMetric('openDefects'), activeMetric('openDefects'))}</Grid>
                        <Grid item xs={6} md={3}>{reviewMetric('Rework', row.performance.reworkCount, undefined, () => toggleMetric('rework'), activeMetric('rework'))}</Grid>
                        <Grid item xs={6} md={3}>
                          {reviewMetric(
                            'Payment',
                            formatCurrency(paymentTotals.totalPaid),
                            `${formatCurrency(paymentTotals.totalApproved)} approved`,
                            () => toggleMetric('payment'),
                            activeMetric('payment')
                          )}
                        </Grid>
                      </Grid>
                      {renderTeamDrilldown(row)}
                    </AccordionDetails>
                  </Accordion>
                );
              }) : (
                <Typography variant="body2" color="text.secondary">No team performance reviews recorded.</Typography>
              )}
              {bonusRows.length > 0 && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.25, py: 1, bgcolor: 'action.hover' }}>
                    <Typography fontWeight={900}>Bonus Points</Typography>
                  </Box>
                  <Table size="small" sx={{ '& td, & th': { py: 0.55, px: 1, fontSize: 12 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Team</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Quality</TableCell>
                        <TableCell align="right">Speed</TableCell>
                        <TableCell align="right">Attendance</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bonusRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.teamName ?? 'Team'}</TableCell>
                          <TableCell>{formatDate(row.periodStart)} - {formatDate(row.periodEnd)}</TableCell>
                          <TableCell align="right">{row.qualityPoints}</TableCell>
                          <TableCell align="right">{row.speedPoints}</TableCell>
                          <TableCell align="right">{row.attendancePoints}</TableCell>
                          <TableCell align="right"><Typography fontWeight={900}>{row.totalPoints}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {renderMaterialResourcesReview()}

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
              <Typography fontWeight={900}>Site Inspection Reviews</Typography>
              <Chip size="small" label={inspectionRows.length} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {inspectionRows.length ? inspectionRows.map((row) => (
                <Accordion key={row.id} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      sx={{ width: '100%', pr: 1 }}
                    >
                      <Box>
                        <Typography fontWeight={900}>{row.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.inspectionNumber} | {humanize(row.inspectionType)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={humanize(row.status)} />
                        <Chip size="small" label={row.scheduledAt ? formatDate(row.scheduledAt) : 'No scheduled date'} />
                        {row.score !== null && <Chip size="small" label={`Score ${row.score}`} />}
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={6} md={3}>{reviewMetric('Status', humanize(row.status) || 'Unknown')}</Grid>
                      <Grid item xs={6} md={3}>{reviewMetric('Scheduled', row.scheduledAt ? formatDate(row.scheduledAt) : 'No date')}</Grid>
                      <Grid item xs={6} md={3}>{reviewMetric('Started', row.startedAt ? formatDate(row.startedAt) : 'Not started')}</Grid>
                      <Grid item xs={6} md={3}>{reviewMetric('Completed', row.completedAt ? formatDate(row.completedAt) : 'Not completed')}</Grid>
                      <Grid item xs={12}>
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Summary</Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {row.resultSummary || 'No inspection summary has been recorded yet.'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )) : (
                <Typography variant="body2" color="text.secondary">No site inspection reviews recorded.</Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
              <Typography fontWeight={900}>Tests & Permits</Typography>
              <Chip size="small" label={permitRows.length} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {permitRows.length ? permitRows.map((row) => (
                <Accordion key={row.id} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      sx={{ width: '100%', pr: 1 }}
                    >
                      <Box>
                        <Typography fontWeight={900}>{row.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.permitNumber} | {humanize(row.permitType)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={humanize(row.status)} />
                        <Chip size="small" label={row.expiryDate ? `Expires ${formatDate(row.expiryDate)}` : 'No expiry'} />
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={4}>{reviewMetric('Authority', row.issuingAuthority || 'Not recorded')}</Grid>
                      <Grid item xs={6} md={2}>{reviewMetric('Applied', row.applicationDate ? formatDate(row.applicationDate) : 'No date')}</Grid>
                      <Grid item xs={6} md={2}>{reviewMetric('Issued', row.issueDate ? formatDate(row.issueDate) : 'No date')}</Grid>
                      <Grid item xs={6} md={2}>{reviewMetric('Expiry', row.expiryDate ? formatDate(row.expiryDate) : 'No date')}</Grid>
                      <Grid item xs={6} md={2}>{reviewMetric('Conditions', row.conditions?.length ?? 0)}</Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )) : (
                <Typography variant="body2" color="text.secondary">No active tests or permits recorded.</Typography>
              )}
              </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      {pmPersonnelContextCard}
      <Typography variant="h4" fontWeight={900}>Project Management</Typography>
      {errorAlert}
      <Grid container spacing={1}>
        <Grid item xs={3}>{metric('Resource readiness', materialsReadyPercent === null ? '—' : `${materialsReadyPercent}%`, 'materials ready')}</Grid>
        <Grid item xs={3}>{metric('Team allocation', activeTeams.length ? `${workingTeams.length}/${activeTeams.length}` : '—', 'teams with work')}</Grid>
        <Grid item xs={3}>{metric('Tests & permits', String(activePermits.length), 'active')}</Grid>
        <Grid item xs={3}>{metric('Blocked tasks', String(blockedCount), 'need action')}</Grid>
      </Grid>
      <Box>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Scheduled tasks to execute this week</Typography>
          <Typography variant="caption" fontWeight={800}>
            {thisWeekTasks.length ? `${readinessPercent}% ready` : 'No tasks due this week'}
          </Typography>
        </Stack>
        <LinearProgress variant="determinate" value={readinessPercent} sx={{ height: 8, borderRadius: 1, mt: 0.5 }} />
      </Box>
      <Typography fontWeight={900}>Resource Readiness</Typography>
      {menuBar('Planning / Look-Ahead Board', 'Tasks within selected period and linked resources', '/field/pm/lookahead')}
      {menuBar('Issued Tasks', 'Supervisor handoffs with issue date, supervisor, and materials', '/field/pm/issued')}
      {menuBar('Submitted Field Reports', 'Supervisor reports waiting for PM review', '/field/pm/submitted-reports')}
      {menuBar('Reviews, Tests & Permits', 'Team performance, site inspections, permits', '/field/pm/reviews')}
      {menuBar('Task Dispatch Board', 'Ready, blocked, in progress, inspection queues', '/field/pm/dispatch')}
    </Stack>
  );
}
